/**
 * Bulk confirmation for the rollout panel (LOCKER-408 DoD5).
 *
 * It NAMES the organizations that will change and keeps them separate from the
 * selected rows already in the target state. The dialog it replaces quoted a
 * single count derived from whatever the search box held — so an empty search
 * meant "every organization", with nothing on screen saying so, and the number
 * did not distinguish writes from no-ops.
 */
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sl2Btn } from "../Sl2Btn"
import { FeatureRolloutBulkPlan } from "./featureRolloutModel"

/**
 * How many names to print before collapsing to "+N more".
 *
 * A confirmation an operator cannot read is not a confirmation, and a
 * selection of 400 would otherwise render a scroll box nobody checks.
 */
const NAMES_SHOWN = 12

const NameList: FC<{ names: string[] }> = ({ names }) => {
  const { t } = useTranslation()
  const shown = names.slice(0, NAMES_SHOWN)
  const rest = names.length - shown.length
  return (
    <Stack
      component="ul"
      spacing={0.25}
      sx={{ m: 0, pl: 2.5, maxHeight: 200, overflowY: "auto" }}
    >
      {shown.map((n) => (
        <Typography component="li" key={n} variant="body2">
          {n}
        </Typography>
      ))}
      {rest > 0 && (
        <Typography
          component="li"
          variant="body2"
          color="text.secondary"
          sx={{ listStyle: "none", ml: -2.5 }}
        >
          {t("feature.andNMore", { count: rest })}
        </Typography>
      )}
    </Stack>
  )
}

type FeatureBulkConfirmDialogProps = {
  plan: FeatureRolloutBulkPlan | null
  isBusy: boolean
  onCancel: () => void
  onConfirm: () => void
}

export const FeatureBulkConfirmDialog: FC<FeatureBulkConfirmDialogProps> = ({
  plan,
  isBusy,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const activating = plan?.action === "activate"
  const changing = plan?.changing || []
  const unchanged = plan?.unchanged || []

  return (
    <Dialog data-ui="UI-SL2W-274"
      open={!!plan}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      data-testid="feature-bulk-confirm"
    >
      <DialogTitle>
        {activating
          ? t("feature.confirmActivateTitle")
          : t("feature.confirmDeactivateTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {changing.length === 0 ? (
            <Alert severity="info">{t("feature.bulkNothingToChange")}</Alert>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={600}>
                {activating
                  ? t("feature.bulkWillActivate", { count: changing.length })
                  : t("feature.bulkWillDeactivate", { count: changing.length })}
              </Typography>
              <NameList names={changing.map((r) => r.name)} />
            </Stack>
          )}

          {unchanged.length > 0 && (
            <Stack spacing={1}>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                {t("feature.bulkAlreadyInState", { count: unchanged.length })}
              </Typography>
              <NameList names={unchanged.map((r) => r.name)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Sl2Btn variant="outlined" onClick={onCancel} disabled={isBusy}>
          {t("cancel")}
        </Sl2Btn>
        <Sl2Btn
          variant="contained"
          color={activating ? "primary" : "warning"}
          onClick={onConfirm}
          disabled={isBusy || changing.length === 0}
          data-testid="feature-bulk-confirm-apply"
        >
          {isBusy ? t("saving") : activating ? t("activate") : t("deactivate")}
        </Sl2Btn>
      </DialogActions>
    </Dialog>
  )
}

export default FeatureBulkConfirmDialog
